/**
 * Defines the specifications (contracts) for all server business logic flows.
 * Imports `ChatSpec` for use in flow outputs.
 * Imports message schemas for flow inputs and chunks.
 *
 * This file is a cornerstone of the project's CQRS (Command Query Responsibility Segregation) architecture.
 * It defines explicit, type-safe contracts for every command that the client can issue to the server.
 *
 * Each "flow" (e.g., `SendMessage`, `CreateChat`) is defined using `defineFlowSpec`, which creates a `FlowSpec` object.
 * A `FlowSpec` is a self-contained contract that specifies:
 * 1.  `name`: A unique string identifier for the flow.
 * 2.  `input`: A Zod schema for the data the client must send.
 * 3.  `output`: A Zod schema for the final data the server will return.
 * 4.  `chunk`: A Zod schema for the data chunks that are streamed back to the client during processing.
 *
 * This approach provides strong guarantees about the client-server communication, making the system robust,
 * type-safe, and easier to debug. It serves as a single source of truth for the API surface.
 */

import { z } from 'zod';

export type * from 'zod';

/**
 * The core interface for a flow specification. It defines the contract for a server operation.
 * @template Name - The literal string name of the flow.
 */
export interface FlowSpec<
  Name extends string,
  InputSchema extends z.ZodTypeAny,
  OutputSchema extends z.ZodTypeAny,
  ChunkSchema extends z.ZodTypeAny,
> {
  InputType: z.output<InputSchema>;
  OutputType: z.output<OutputSchema>;
  ChunkType: z.output<ChunkSchema>;
  /** Name of the flow, used for identification and routing. */
  name: Name;
  /** Schema of the input to the flow. */
  input: InputSchema;
  /** Schema of the output from the flow. */
  output: OutputSchema;
  /** Schema of the streaming chunks from the flow. */
  chunk: ChunkSchema;
  /** Metadata of the flow used by tooling. */
  metadata: Record<string, unknown>;

  /** Whether the flow requires a privileged user to run. */
  privileged: boolean;
  /** Optional Firebase Auth custom claims that can satisfy a privileged flow gate. */
  claims?: string[];
  /**
   * Whether the flow is a "sensitive action" that must be blocked while the user
   * has pending legal-policy re-acceptance. Enforced server-side as defense-in-depth
   * against a client that skips the inline re-acceptance dialog. Defaults to false.
   */
  requiresPolicyAcceptance?: boolean;
  /**
   * Whether a member whose membership has lapsed may still run this flow.
   *
   * The subscription gate is the inverse of the policy gate above: it is ON by default and turned
   * OFF here, per flow. Defaulting to gated is the only arrangement that stays correct as flows are
   * added — a flow written a year from now is protected by having been written, not by someone
   * remembering a flag. The opt-in arrangement fails the other way, and silently.
   *
   * Set it on the handful of flows a locked member must still reach: the billing remedies (which are
   * how they get unlocked), the signup steps that run before a membership exists, and the exits
   * (accepting revised policies, deleting the account).
   */
  allowsLockedAccount?: boolean;
  /**
   * Whether running the flow twice has the same effect as running it once, so the client transport
   * may resend it once when the request fails at the network level (ELARA-1146).
   *
   * A network-level failure says nothing about whether the server received the request: a phone
   * resuming from the background often fails its first request on a connection iOS tore down while
   * the app was suspended, and that request may or may not have landed. Resending is only safe when
   * a duplicate is harmless, so this is opt-in and reserved for reads and the warmup. Never set it on
   * a flow that sends a message, books, charges, or otherwise mutates state.
   */
  idempotent?: boolean;
}

/**
 * A utility interface representing any `FlowSpec`.
 */
export interface AnyFlowSpec extends FlowSpec<string, z.ZodTypeAny, z.ZodTypeAny, z.ZodTypeAny> {}

export const MAX_ENDPOINT_NAME_LENGTH = 63;

export const FLOW_ENDPOINT_PREFIX = 'endpoints';

export const MAX_FLOW_NAME_LENGTH = MAX_ENDPOINT_NAME_LENGTH - FLOW_ENDPOINT_PREFIX.length - 1;

/**
 * Creates a canonical endpoint name for a flow, formatted as `${prefix}-${name}_${suffix}`.
 * If `prefix` is not provided, the canonical name is returned as `${name}_${suffix}`.
 * If `suffix` is not provided, the canonical name is returned as `${name}`.
 * The canonical name is always at most `MAX_ENDPOINT_NAME_LENGTH` characters long.
 * @param name - The name of the flow.
 * @param suffix - The suffix to add to the name.
 * @param prefix - The prefix to add to the name.
 * @returns The canonical endpoint name.
 */
export function makeCanonicalEndpointName({
  name,
  suffix,
  prefix,
}: {
  name: string;
  suffix?: string;
  prefix?: string;
}): string {
  suffix = suffix?.trim();
  const canonicalName = suffix ? `${name}_${suffix}`.slice(0, MAX_FLOW_NAME_LENGTH) : name;
  if (!prefix) return canonicalName;
  return `${prefix}-${canonicalName}`.slice(0, MAX_ENDPOINT_NAME_LENGTH);
}

/**
 * The primary factory function for creating a `FlowSpec`.
 * It takes a partial spec and merges it with sensible defaults.
 *
 * @param input
 * @param output
 * @param chunk
 * @param metadata
 * @param authorized
 * @param spec - The partial specification for the flow. Must include a `name`.
 * @returns The complete, defaulted `FlowSpec` object.
 */
export function defineFlowSpec<
  Name extends string,
  InputSchema extends z.ZodTypeAny,
  OutputSchema extends z.ZodTypeAny,
  ChunkSchema extends z.ZodTypeAny,
>({
  input = z.void() as unknown as InputSchema,
  output = z.unknown() as unknown as OutputSchema,
  chunk = z.void() as unknown as ChunkSchema,
  metadata = {},
  privileged = false,
  claims = undefined,
  requiresPolicyAcceptance = false,
  allowsLockedAccount = false,
  idempotent = false,
  ...spec
}: { name: Name } & Partial<FlowSpec<Name, InputSchema, OutputSchema, ChunkSchema>>): FlowSpec<
  Name,
  InputSchema,
  OutputSchema,
  ChunkSchema
> {
  return {
    input,
    output,
    chunk,
    metadata,
    privileged,
    claims,
    requiresPolicyAcceptance,
    allowsLockedAccount,
    idempotent,
    ...spec,
  } as FlowSpec<
    Name,
    InputSchema,
    OutputSchema,
    ChunkSchema
  >;
}

export const FlowErrorSchema = z.object({
  failed: z.literal(true),
  error: z.string(),
  details: z.record(z.string(), z.unknown()),
});

export interface FlowError extends z.infer<typeof FlowErrorSchema> {}

export function isFlowError(error: unknown): error is FlowError {
  return FlowErrorSchema.safeParse(error).success;
}
